"use client";

import Image from "next/image";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

type JourneyEntry = {
  label: string;
  image: string;
  alt: string;
  title: string;
  description: string;
  layout: "left" | "right";
  objectPosition: string;
};

const journeyEntries: JourneyEntry[] = [
  {
    label: "첫 평가",
    image: "/images/motiona-v2/21-movement-evaluation.webp",
    alt: "센터에서 자세와 움직임을 확인하는 첫 평가 장면",
    title: "지금 가능한 움직임부터 확인합니다",
    description:
      "아픈 부위만 묻지 않습니다. 서기, 걷기, 팔 쓰기처럼 일상에서 막히는 움직임을 함께 확인합니다.",
    layout: "left",
    objectPosition: "center bottom",
  },
  {
    label: "센터 운동",
    image: "/images/motiona-v2/11-neuro-weight-shift.webp",
    alt: "센터에서 체중지지와 몸통 조절을 연습하는 재활운동 장면",
    title: "센터에서는 필요한 동작부터 정확히 연습합니다",
    description:
      "몸통 조절과 체중지지를 다시 연결합니다. 많이 하기보다 정확히 시작하는 데 집중합니다.",
    layout: "right",
    objectPosition: "48% center",
  },
  {
    label: "통증 조절",
    image: "/images/motiona-v2/19-neck-shoulder.webp",
    alt: "좌식에서 목과 어깨 움직임을 조절하는 재활운동 장면",
    title: "통증은 움직임의 습관까지 함께 봅니다",
    description:
      "허리, 어깨, 무릎이 불편해도 부담의 시작점은 다른 곳에 있을 수 있습니다. 몸이 피하는 방식을 찾아 운동 방향을 맞춥니다.",
    layout: "left",
    objectPosition: "center 44%",
  },
  {
    label: "방문재활",
    image: "/images/motiona-v2/15-wheelchair-stand.webp",
    alt: "휠체어 옆에서 일어서기와 체중지지를 훈련하는 방문재활 장면",
    title: "집에서도 회복 흐름을 이어갑니다",
    description:
      "센터 방문이 어렵다면 생활공간에서 기립과 이동을 확인합니다. 실제 하루에 필요한 움직임으로 연습합니다.",
    layout: "right",
    objectPosition: "center 46%",
  },
  {
    label: "변화 확인",
    image: "/images/motiona-v2/23-scapula-example.webp",
    alt: "어깨와 견갑 조절 변화를 전후로 비교하는 예시",
    title: "변화는 기록하며 다시 확인합니다",
    description:
      "운동 후 자세와 움직임을 다시 봅니다. 좋아진 부분과 다음에 살필 부분을 함께 정리합니다.",
    layout: "left",
    objectPosition: "center 48%",
  },
];

export function RecoveryJourneyTimeline() {
  return (
    <section className="relative overflow-hidden border-y-2 border-[#050505] bg-[#fff7d6] px-4 py-18 md:px-6 md:py-24">
      <div className="mx-auto max-w-[1220px]">
        <div className="mb-12 grid gap-6 md:mb-16 md:grid-cols-[0.78fr_0.52fr] md:items-end">
          <div>
            <p className="inline-flex rounded-full border-2 border-[#050505] bg-[#ffc917] px-4 py-2 text-xs font-black text-[#050505] shadow-[3px_3px_0_#8c0000]">
              회복 타임라인
            </p>
            <h2 className="mt-5 text-[2.45rem] font-black leading-[1.08] text-[#050505] md:text-[4.9rem]">
              회복은 병원 밖 일상에서도 이어져야 합니다
            </h2>
          </div>
          <p className="max-w-xl text-base font-bold leading-8 text-[#4d3a00] md:justify-self-end md:text-lg">
            현재 상태를 확인하고 센터와 집에서 이어갈 운동 순서를 단계별로 정리합니다.
          </p>
        </div>

        <div className="relative">
          <div
            className="absolute bottom-0 left-4 top-0 w-[2px] bg-[#050505]/18 md:left-1/2 md:-translate-x-1/2"
            aria-hidden="true"
          />
          <div
            className="absolute left-4 top-0 h-24 w-[2px] bg-[#f00000] md:left-1/2 md:-translate-x-1/2"
            aria-hidden="true"
          />
          {journeyEntries.map((entry, index) => (
            <TimelineItem key={entry.label} entry={entry} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TimelineItem({ entry, index }: { entry: JourneyEntry; index: number }) {
  const itemRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: itemRef,
    offset: ["start center", "end center"],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.78, 1], [0.72, 1, 1, 0.72]);
  const scale = useTransform(scrollYProgress, [0, 0.25, 0.78, 1], [0.98, 1, 1, 0.98]);
  const isLeft = entry.layout === "left";
  const stepNumber = String(index + 1).padStart(2, "0");

  return (
    <motion.article
      ref={itemRef}
      style={{ opacity, scale }}
      className="relative mb-10 pl-10 last:mb-0 md:mb-16 md:pl-0"
    >
      <div
        className="absolute left-0 top-7 z-10 flex size-8 items-center justify-center rounded-full border-2 border-[#050505] bg-[#ffc917] text-[0.68rem] font-black text-[#050505] shadow-[3px_3px_0_#8c0000] md:left-1/2 md:top-1/2 md:size-10 md:-translate-x-1/2 md:-translate-y-1/2"
        aria-hidden="true"
      >
        {stepNumber}
      </div>

      <div className={`grid gap-4 md:grid-cols-2 md:items-center md:gap-16 ${isLeft ? "md:text-right" : ""}`}>
        <div className={`${isLeft ? "md:order-2" : "md:order-1"}`}>
          <div className="relative overflow-hidden rounded-[8px] border-2 border-[#050505] bg-[#fff0a8] shadow-[7px_7px_0_rgba(140,0,0,0.16)]">
            <div className="relative aspect-[16/11]">
              <Image
                src={entry.image}
                alt={entry.alt}
                fill
                sizes="(min-width: 1024px) 42vw, 100vw"
                className="object-cover transition duration-700 hover:scale-[1.035]"
                style={{ objectPosition: entry.objectPosition }}
              />
            </div>
            <div className="absolute left-3 top-3 rounded-full border border-[#050505] bg-[#fffdf2] px-3 py-1 text-xs font-black text-[#8c0000]">
              {entry.label}
            </div>
          </div>
        </div>

        <div className={`${isLeft ? "md:order-1 md:flex md:justify-end" : "md:order-2"}`}>
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
            className="rounded-[8px] border-2 border-[#050505] bg-[#fffdf2] p-5 text-left shadow-[7px_7px_0_rgba(5,5,5,0.1)] md:max-w-[520px] md:p-7"
          >
            <p className="text-xs font-black text-[#8c0000]">
              STEP {stepNumber}
            </p>
            <h3 className="mt-3 text-3xl font-black leading-[1.12] text-[#050505] md:text-[2.75rem]">
              {entry.title}
            </h3>
            <p className="mt-4 text-base font-semibold leading-8 text-[#4d3a00] md:text-lg">
              {entry.description}
            </p>
          </motion.div>
        </div>
      </div>
    </motion.article>
  );
}
